import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Search } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCurrency } from "@/context/CurrencyContext";
import api, { mediaUrl } from "@/lib/api";
import { TID } from "@/lib/testIds";

const SearchOverlay = () => {
  const { searchOpen, setSearchOpen } = useStore();
  const { format } = useCurrency();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();

  useEffect(() => { if (!searchOpen) { setQ(""); setRes(null); } }, [searchOpen]);

  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = useCallback((path) => { setSearchOpen(false); nav(path); }, [nav, setSearchOpen]);

  if (!searchOpen) return null;
  const empty = res && !res.products.length && !res.collections.length && !res.articles.length;

  return (
    <div className="fixed inset-0 z-50 bg-ivory fade-up overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 pt-8 pb-24">
        <div className="flex items-center justify-between mb-10">
          <p className="overline text-espresso/50">Search</p>
          <button onClick={() => setSearchOpen(false)} aria-label="Close"><X strokeWidth={1.4} /></button>
        </div>
        <div className="flex items-center border-b-2 border-espresso pb-3">
          <Search strokeWidth={1.4} className="text-espresso/50" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={TID.searchInput}
            placeholder="Search pieces, collections, stories"
            className="flex-1 bg-transparent text-2xl md:text-3xl font-serif px-4 outline-none placeholder:text-espresso/30" />
        </div>

        {empty && (
          <div className="mt-16 text-center">
            <p className="font-serif text-4xl mb-4">We couldn't find her.</p>
            <div className="flex flex-wrap justify-center gap-3 text-[0.72rem] tracking-[0.18em] uppercase">
              {[["New In", "/collections/new-in"], ["ORI", "/collections/ori"], ["Women", "/collections/women"]].map(([l, h]) => (
                <button key={h} onClick={() => go(h)} className="border border-espresso px-5 py-2.5 hover:bg-espresso hover:text-cream transition-colors">{l}</button>
              ))}
            </div>
          </div>
        )}

        {res && res.products.length > 0 && (
          <div className="mt-10">
            <p className="overline text-espresso/50 mb-5">Pieces</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {res.products.map((p) => (
                <button key={p.id} onClick={() => go(`/products/${p.slug}`)} className="text-left">
                  <div className="bg-sand aspect-[3/4] overflow-hidden mb-2"><img src={mediaUrl(p.colors?.[0]?.main_image)} alt={p.name} className="w-full h-full object-contain" /></div>
                  <p className="font-serif text-base">{p.name}</p>
                  <p className="text-xs text-espresso/50">{format(p.price)}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {res && (res.collections.length > 0 || res.articles.length > 0) && (
          <div className="mt-10 grid md:grid-cols-2 gap-8">
            {res.collections.length > 0 && (
              <div><p className="overline text-espresso/50 mb-3">Collections</p>
                {res.collections.map((c) => <button key={c.id} onClick={() => go(`/collections/${c.slug}`)} className="block font-serif text-xl hover-underline mb-1">{c.name}</button>)}
              </div>
            )}
            {res.articles.length > 0 && (
              <div><p className="overline text-espresso/50 mb-3">Journal</p>
                {res.articles.map((a) => <button key={a.id} onClick={() => go(`/journal/${a.slug}`)} className="block font-serif text-xl hover-underline mb-1 text-left">{a.title}</button>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchOverlay;
